// @flow strict

import React from "react";
import { imgSrc } from "~common/utils";
import { playersMap } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import type {
  RegisteredStore,
  Equipo,
  LocalSonidos,
  SonidoIdx,
  Sonidos,
} from "~robin-hood/redux/store";
import {
  equipoWithPrueba,
  localStoreWithPrueba,
} from "~robin-hood/redux/store";
import classnames from "~common/classnames";
import styles from "./PruebaSonidos.module.css";
// $FlowFixMe[untyped-import]
import visualizer from "~robin-hood/animations/visualizer.module.scss";
import window from "~robin-hood/animations/window.module.css";

const BASE_IMG_PATH = "/images/robin-hood/prueba-sonidos";
function sonidoSrc(path: string): string {
  return imgSrc(BASE_IMG_PATH + path, "noPreload");
}
const SONIDOS_ICONS = [
  sonidoSrc("/PHONE.png"),
  sonidoSrc("/RING.png"),
  sonidoSrc("/COIN.png"),
  sonidoSrc("/SIREN.png"),
];
const SONIDOS_SECRET: Sonidos = [4, 10, 9, 9];

const SONIDOS_AUDIOS = playersMap([
  sonidoSrc("/sonido1corto.mp3"),
  sonidoSrc("/sonido2corto.mp3"),
  sonidoSrc("/sonido3corto.mp3"),
  sonidoSrc("/sonido4corto.mp3"),
  sonidoSrc("/sonido5corto.mp3"),
  sonidoSrc("/sonido6corto.mp3"),
]);

type Props = {
  player: Player,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
  store: RegisteredStore,
  updateLocalStore: (store: RegisteredStore) => void,
};

export function PruebaSonidos(props: Props): React$Node {
  const { player, store, updateLocalStore, equipo, updateEquipo } = props;
  const [playing, setPlaying] = React.useState<boolean>(false);
  return (
    <div className={classnames(styles.pruebaSonidosWrapper, window.scaleInBl)}>
      <div className={classnames(styles.pruebaSonidos)}>
        <div className={styles.imgWrapper}>
          <div className={styles.pruebaControls}>
            <span className={styles.red}></span>
            <span className={styles.yellow}></span>
            <span className={styles.green}></span>
            <span className={classnames(styles.plus, "fa fa-plus")}></span>
          </div>
          <div className={styles.visualizerWrapper}>
            <div
              className={classnames(
                visualizer.visualizer,
                playing && visualizer.play
              )}
            >
              <div className={visualizer.visualizerBar1}></div>
              <div className={visualizer.visualizerBar2}></div>
              <div className={visualizer.visualizerBar3}></div>
              <div className={visualizer.visualizerBar1}></div>
              <div className={visualizer.visualizerBar2}></div>
            </div>
          </div>
          <audio
            key={player}
            onPlay={() => {
              console.log("playing !!!");
              setPlaying(true);
            }}
            onPause={() => {
              console.log("paused !!!");
              setPlaying(false);
            }}
            className={styles.audio}
            controls
          >
            <source src={SONIDOS_AUDIOS.get(player)} type="audio/mpeg" />
          </audio>
        </div>
      </div>
      <Solucion
        player={player}
        equipo={equipo}
        updateEquipo={updateEquipo}
        store={store}
        updateLocalStore={updateLocalStore}
      />
    </div>
  );
}

const DEFAULT_SONIDOS: Sonidos = [0, 0, 0, 0];

function valueFromLocal(store: RegisteredStore, player: Player): Sonidos {
  const sonidos = store.local?.sonidos;
  return (
    (sonidos && sonidos[player] && sonidos[player].value) || DEFAULT_SONIDOS
  );
}

function valueFromEquipo(equipo: Equipo): Sonidos {
  return equipo.pruebas?.sonidos?.value || DEFAULT_SONIDOS;
}
function correctFromEquipo(equipo: Equipo): boolean {
  return equipo.pruebas?.sonidos?.correct || false;
}

function updateLocalWithSonidos(
  store: RegisteredStore,
  player: Player,
  value: LocalSonidos
): RegisteredStore {
  return localStoreWithPrueba(store, "sonidos", {
    [(player: string)]: value,
  });
}

type SolucionProps = {
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
};

export class Solucion extends React.PureComponent<SolucionProps> {
  _doCheck = () => {
    const { store, equipo, updateEquipo, player } = this.props;
    const sonidos = valueFromLocal(store, player);
    const isCorrect = correctFromEquipo(equipo);
    const correct = SONIDOS_SECRET.every((s, idx) => s === sonidos[idx]);
    if (correct && correct !== isCorrect) {
      const updated = equipoWithPrueba(equipo, {
        sonidos: { value: sonidos, correct },
      });
      updateEquipo(updated);
    }
  };

  // On change we won't connect with server
  _onSonidoChange = (idx: 0 | 1 | 2 | 3): ((number) => void) => {
    return (value: number) => {
      const { store, updateLocalStore, player } = this.props;
      const values = valueFromLocal(store, player);
      if (values[idx] === value) {
        return;
      }
      const newValues = [...values];
      newValues[idx] = value;
      updateLocalStore(
        updateLocalWithSonidos(store, player, { value: newValues })
      );
    };
  };

  render(): React$Node {
    const { equipo, store, player } = this.props;
    const correct = correctFromEquipo(equipo);
    const values = correct
      ? valueFromEquipo(equipo)
      : valueFromLocal(store, player);
    return (
      <div
        className={classnames(
          styles.solucionWrapper,
          correct && styles.solucionCorrect
        )}
      >
        <div className={styles.solucion}>
          <div className={styles.solucionSonidoRow}>
            <SolucionSonido
              correct={correct}
              idx={0}
              value={values[0]}
              onChange={this._onSonidoChange(0)}
            />
            <SolucionSonido
              correct={correct}
              idx={1}
              value={values[1]}
              onChange={this._onSonidoChange(1)}
            />
          </div>
          <div className={styles.solucionSonidoRow}>
            <SolucionSonido
              correct={correct}
              idx={2}
              value={values[2]}
              onChange={this._onSonidoChange(2)}
            />
            <SolucionSonido
              correct={correct}
              idx={3}
              value={values[3]}
              onChange={this._onSonidoChange(3)}
            />
          </div>
          <SolucionCheck correct={correct} onCheck={this._doCheck} />
        </div>
      </div>
    );
  }
}

type CheckProps = {
  correct: boolean,
  onCheck: () => void,
};
const SPINNER = {
  frames: [0],
  interval: 1000,
};
const RESET = 2000;

function next(idx: ?number): number {
  return ((idx ?? 0) + 1) % SPINNER.frames.length;
}

class SolucionCheck extends React.PureComponent<
  CheckProps,
  { checking?: number, checked: boolean, tick: number }
> {
  _mounted: boolean;
  _timeout: ?TimeoutID;
  state = { checking: undefined, checked: !!this.props.correct, tick: 0 };

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  _tick = () => {
    if (!this._mounted) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
      return;
    }
    this.setState(
      (state) => ({
        tick: state.tick + 1,
        checking: next(state.checking),
      }),
      () => {
        if (!this._mounted || this._timeout) return;
        const TICKS = SPINNER.frames.length * 3;
        if (this.state.tick === TICKS - 1) {
          // Check early so result is ready
          this.props.onCheck();
        }
        if (this.state.tick < TICKS) {
          this._timeout = setTimeout(() => {
            this._timeout = undefined;
            this._tick();
          }, SPINNER.interval);
        } else {
          // Finished
          this.setState(
            {
              tick: 0,
              checking: undefined,
              checked: true,
            },
            () => {
              this._timeout = setTimeout(() => {
                this.setState({ checked: false });
                this._timeout = undefined;
              }, RESET);
            }
          );
        }
      }
    );
  };

  _onClick = () => {
    const { correct } = this.props;
    const { checking, checked } = this.state;
    if (correct || checking || this._timeout) return;
    if (checked) {
      this.setState({ checked: false });
    } else {
      this._tick();
    }
  };

  render() {
    const { correct } = this.props;
    const { checking, checked } = this.state;
    let content: React$Node, className: string;
    if (correct) {
      content = "OK";
      className = styles.solucionCorrect;
    } else if (checking != null) {
      className = styles.solucionChecking;
    } else if (checked) {
      content = "NO";
      className = styles.solucionIncorrect;
    } else {
      content = <span className="fa fa-question" />;
    }
    return (
      <div
        className={classnames(
          className,
          styles.solucionSonidos,
          styles.solucionCheck,
          styles.button
        )}
        onClick={this._onClick}
      >
        {content}
      </div>
    );
  }
}
const MAX_SONIDO = 20;

type SolucionSonidoProps = {
  correct: boolean,
  idx: SonidoIdx,
  value: number,
  onChange: (number) => void,
};

function SolucionSonido(props: SolucionSonidoProps) {
  return (
    <div className={classnames(styles.solucionSonido)}>
      <div className={styles.solucionSonidoIcon}>
        <img src={SONIDOS_ICONS[props.idx]} />
      </div>
      <div className={styles.solucionSonidoValue}>
        <span
          className={classnames("fa fa-caret-up", styles.buttonUp)}
          onClick={() => {
            props.onChange &&
              props.onChange((props.value + MAX_SONIDO + 1) % MAX_SONIDO);
          }}
        />
        {props.value}
        <span
          className={classnames("fa fa-caret-down", styles.buttonDown)}
          onClick={() => {
            props.onChange &&
              props.onChange((props.value + MAX_SONIDO - 1) % MAX_SONIDO);
          }}
        />
      </div>
    </div>
  );
}
